import { addSquareBorder } from "../../../../utils/addSquareBorder";
import { processDateJS } from "../../../../utils/dateProcessor";
import {
  addNumberFormatToColumns,
  alignCellsHorizontalCenter,
  colourCells,
  mergeSheetCells,
} from "./helpers";

export const addBorderToCells = (worksheet, cells, weight = "medium") => {
  cells.forEach((cell) => {
    worksheet.getCell(cell).border = {
      top: { style: weight },
      left: { style: weight },
      bottom: { style: weight },
      right: { style: weight },
    };
  });
};

export const generateCellsRange = (startRow, numOfRows, columns) => {
  const list = [];

  for (let i = 0; i < numOfRows; i++) {
    const row = startRow + i;
    columns.forEach((col) => {
      list.push(`${col}${row}`);
    });
  }

  return list;
};

export const styleCells = (worksheet, config) => {
  const { styles, name } = config;

  if (styles.singleCellBorders) {
    addBorderToCells(worksheet, styles.singleCellBorders);
  }

  if (styles.squareBorders) {
    styles.squareBorders.forEach((borderGroup) => {
      const { start, end } = borderGroup;
      addSquareBorder(worksheet, start, end, start, end, "medium");
    });
  }

  if (styles.mergedCells) {
    mergeSheetCells(worksheet, styles.mergedCells);
  }

  if (styles.borderRightFixes) {
    styles.borderRightFixes.forEach((cell) => {
      worksheet.getCell(cell).border = {
        ...worksheet.getCell(cell).border,
        right: { style: "medium" },
      };
    });
  }

  if (styles.removedBorders) {
    Object.keys(styles.removedBorders).forEach((cell) => {
      styles.removedBorders[cell].forEach((position) => {
        worksheet.getCell(cell).border = {
          ...worksheet.getCell(cell).border,
          [position]: undefined,
        };
      });
    });
  }

  if (styles.boldCells) {
    styles.boldCells.forEach((cell) => {
      worksheet.getCell(cell).font = {
        bold: true,
      };
    });
  }

  if (styles.dateCells) {
    styles.dateCells.forEach((cell) => {
      worksheet.getCell(cell).numFmt = "dd/mm/yyyy";
    });
  }

  if (styles.colWidths) {
    Object.keys(styles.colWidths).forEach((col) => {
      worksheet.getColumn(Number(col)).width = styles.colWidths[col];
    });
  }

  if (styles.rowHeights) {
    Object.keys(styles.rowHeights).forEach((row) => {
      worksheet.getRow(Number(row)).height = styles.rowHeights[row];
    });
  }

  if (styles.alignments) {
    Object.keys(styles.alignments).forEach((cell) => {
      const alignment = styles.alignments[cell];
      worksheet.getCell(cell).alignment = {
        ...worksheet.getCell(cell).alignment,
        ...alignment,
      };
    });
  }

  if (styles.calculations) {
    setCalculationsForCells(worksheet, name);
  }

  if (styles.colouredCells) {
    Object.keys(styles.colouredCells).forEach((colour) => {
      colourCells(worksheet, styles.colouredCells[colour], colour);
    });
  }

  if (styles.horizontallyAlignedCells) {
    alignCellsHorizontalCenter(worksheet, styles.horizontallyAlignedCells);
  }

  if (styles.centeredCols) {
    styles.centeredCols.forEach((col) => {
      worksheet.getColumn(col).alignment = {
        horizontal: "center",
        vertical: "middle",
      };
    });
  }

  if (styles.alignedLeftRows) {
    styles.alignedLeftRows.forEach((row) => {
      worksheet.getRow(row).alignment = { horizontal: "left" };
    });
  }

  if (styles.wrappedTextCells) {
    styles.wrappedTextCells.forEach((cell) => {
      worksheet.getCell(cell).alignment = {
        ...worksheet.getCell(cell).alignment,
        wrapText: true,
      };
    });
  }

  if (styles.numberCells) {
    styles.numberCells.forEach((cell) => {
      worksheet.getCell(cell).numFmt = "#,##0";
    });
  }

  if (styles.numberColumns) {
    const cols = styles.numberColumns.columns;
    const startRow = styles.numberColumns.startRow;
    const numOfRows = styles.numberColumns.numOfRows;

    addNumberFormatToColumns(worksheet, cols, startRow, numOfRows);
  }

  if (styles.percentageCells) {
    styles.percentageCells.forEach((cell) => {
      worksheet.getCell(cell).numFmt = "0.000%";
    });
  }
};

export const getCAPData = (savedData, companyID) => {
  const fullCAPData = savedData?.company_accounting_periods;
  const companyCAP = fullCAPData.filter((CAP) => CAP.company_ID === companyID);

  return companyCAP;
};

export const findAdjustedCapsForCompany = (adjustedCapsData, company_ID) => {
  const adjustedCapsForCompany = adjustedCapsData.filter(
    (cap) => cap.company_ID === company_ID,
  );

  const sortedAdjustedCaps = adjustedCapsForCompany.sort((a, b) => {
    return processDateJS(a?.ap_start_date)?.diff(
      processDateJS(b?.ap_start_date),
      "day",
    );
  });

  return sortedAdjustedCaps;
};

export const getNonConsentingStatus = (poaData, companies) => {
  const nonConsentingCompanies = new Set(
    (poaData?.adjusted_caps ?? [])
      .filter((cap) => cap.non_consenting_company === true)
      .map((cap) => cap.company_ID),
  );

  return new Map(
    (companies ?? []).map((company) => [
      company.company_ID,
      nonConsentingCompanies.has(company.company_ID),
    ]),
  );
};

export const setCalculationsForCells = (worksheet, worksheetName) => {
  if (worksheetName === "(3)_UK_group_companies_") {
    worksheet.getCell("D9").value = {
      formula: "='(1)_Group,_period,_return_type_'!D22",
    };
    worksheet.getCell("H319").value = {
      formula: "=SUM(H16:H315)",
    };
    worksheet.getCell("I319").value = {
      formula: "=SUM(I16:I315)",
    };
    worksheet.getCell("H322").value = {
      formula: "=MAX((H319-I319),0)",
    };
    worksheet.getCell("I322").value = {
      formula: "=MAX((I319-H319),0)",
    };
    worksheet.getCell("J322").value = {
      formula: "=MAX(SUM(J16:J315),0)",
    };
  } else if (worksheetName === "(4)_Group_level_amounts_") {
    worksheet.getCell("C9").value = {
      formula: "='(1)_Group,_period,_return_type_'!D22",
    };
  } else if (worksheetName === "(5)_Allocated_restrictions_") {
    worksheet.getCell("J316").value = {
      formula: "=SUM(H15:J314)",
    };
    worksheet.getCell("J317").value = {
      formula: "='(4)_Group_level_amounts_'!C18",
    };
    worksheet.getCell("D9").value = {
      formula: "='(1)_Group,_period,_return_type_'!D22",
    };
  } else if (worksheetName === "(6)_Allocated_reactivations") {
    worksheet.getCell("E315").value = {
      formula: "=SUM(E14:E313)",
    };
    worksheet.getCell("E316").value = {
      formula: "='(4)_Group_level_amounts_'!C20",
    };
    worksheet.getCell("D9").value = {
      formula: "='(1)_Group,_period,_return_type_'!D22",
    };
  }
};
